import React, { useState, useEffect, useMemo } from 'react';
import { useToast, useDisclosure } from '@chakra-ui/react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList,
  VStack,
  Center,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  HStack,
} from '@chakra-ui/react';
import {
  ViewIcon,
  SearchIcon,
  QuestionIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { format } from 'date-fns';
import { supabase } from '../supabaseClient';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useMenuTitle } from '../hooks/useMenuTitle';

const EmailAuditLog = ({ recipientId }) => {
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('sent_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isContentOpen, setIsContentOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();
  const menuTitle = useMenuTitle();
  const supabase = useSupabaseClient();

  const fetchEmailLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('email_logs')
        .select(`
          *,
          email_templates (
            name
          )
        `);

      // Filter by recipient if provided
      if (recipientId) {
        query = query.eq('recipient_id', recipientId);
      }

      // Apply time filter
      const now = new Date();
      if (timeFilter === 'today') {
        query = query.gte('sent_at', new Date(now.setHours(0, 0, 0, 0)).toISOString());
      } else if (timeFilter === 'week') {
        const weekAgo = new Date(now.setDate(now.getDate() - 7));
        query = query.gte('sent_at', weekAgo.toISOString());
      } else if (timeFilter === 'month') {
        const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
        query = query.gte('sent_at', monthAgo.toISOString());
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // Apply sorting
      query = query.order(sortField, { ascending: sortDirection === 'asc' });

      const { data, error } = await query;

      if (error) throw error;
      setEmailLogs(data || []);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailLogs();
  }, [timeFilter, statusFilter, sortField, sortDirection, recipientId]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <Icon as={FaSort} />;
    return sortDirection === 'asc' ? <Icon as={FaSortUp} /> : <Icon as={FaSortDown} />;
  };

  const filteredLogs = useMemo(() => {
    return emailLogs.filter(log => {
      const searchString = searchQuery.toLowerCase();
      return (
        log.subject?.toLowerCase().includes(searchString) ||
        log.recipient_name?.toLowerCase().includes(searchString) ||
        log.recipient_email?.toLowerCase().includes(searchString) ||
        log.content_text?.toLowerCase().includes(searchString) ||
        log.content_html?.toLowerCase().includes(searchString)
      );
    });
  }, [emailLogs, searchQuery]);

  const getStatusBadge = (status) => {
    const statusColors = {
      sent: 'green',
      failed: 'red',
      pending: 'yellow',
    };
    return (
      <Text color={statusColors[status.toLowerCase()] || 'gray.500'} fontSize="xs">
        {status.toLowerCase()}
      </Text>
    );
  };

  const handleViewContent = (email) => {
    setSelectedEmail(email);
    setIsContentOpen(true);
  };

  const formattedCount = new Intl.NumberFormat('en-US').format(filteredLogs.length);

  return (
    <Box h="100%" display="flex" flexDirection="column">
      <Box>
        <HStack justify="space-between" mb={4} px={4} pt={4}>
          <Heading size="lg">{menuTitle}</Heading>
          <IconButton
            icon={<QuestionIcon />}
            variant="ghost"
            aria-label="Help"
            size="sm"
            onClick={onFilterOpen}
          />
        </HStack>

        <Grid templateColumns="1fr auto 140px 160px" gap={2} px={4} pb={2} alignItems="center">
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search subject, recipient name, recipient email, or email content (hidden)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <InputRightElement>
                <IconButton
                  icon={<CloseIcon />}
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  aria-label="Clear search"
                  onClick={() => setSearchQuery('')}
                />
              </InputRightElement>
            )}
          </InputGroup>

          <Text fontSize="sm" color="gray.600" whiteSpace="nowrap" px={2}>
            {formattedCount} {filteredLogs.length === 1 ? 'email' : 'emails'} found
          </Text>

          <Select
            size="sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </Select>

          <Select
            size="sm"
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </Select>
        </Grid>
      </Box>

      <Box flex="1" overflow="hidden" position="relative">
        <Box overflow="auto" h="100%">
          <Table variant="simple" size="xs">
            <Thead position="sticky" top={0} bg="white" zIndex={1} boxShadow="0 1px 2px rgba(0,0,0,0.1)">
              <Tr>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('sent_at')}>
                  Date {getSortIcon('sent_at')}
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('subject')}>
                  Subject {getSortIcon('subject')}
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('recipient_name')}>
                  Recipient Name {getSortIcon('recipient_name')}
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('recipient_email')}>
                  Recipient Email {getSortIcon('recipient_email')}
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('initiator_name')}>
                  Initiator {getSortIcon('initiator_name')}
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal" cursor="pointer" onClick={() => handleSort('status')}>
                  Status {getSortIcon('status')}
                </Th>
                <Th py={0} px={1} fontSize="2xs" fontWeight="normal">
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr>
                  <Td colSpan={7}>
                    <Center p={4}>
                      <Spinner />
                    </Center>
                  </Td>
                </Tr>
              ) : error ? (
                <Tr>
                  <Td colSpan={7}>
                    <Alert status="error">
                      <AlertIcon />
                      {error}
                    </Alert>
                  </Td>
                </Tr>
              ) : filteredLogs.length === 0 ? (
                <Tr>
                  <Td colSpan={7}>
                    <Alert status="info" variant="subtle">
                      <AlertIcon />
                      <Box>
                        <AlertTitle>No Emails Found</AlertTitle>
                        <AlertDescription>
                          There are no email records available for this contact.
                        </AlertDescription>
                      </Box>
                      <CloseButton position="absolute" right="8px" top="8px" />
                    </Alert>
                  </Td>
                </Tr>
              ) : (
                filteredLogs.map((log) => (
                  <Tr key={log.id} fontSize="xs">
                    <Td py={0} px={1}>{format(new Date(log.sent_at), 'yyyy-MM-dd HH:mm')}</Td>
                    <Td py={0} px={1}>
                      <Box 
                        overflowX="auto" 
                        whiteSpace="nowrap" 
                        maxWidth="300px"
                        sx={{
                          '&::-webkit-scrollbar': {
                            height: '3px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'gray.200',
                            borderRadius: '1px',
                          }
                        }}
                      >
                        {log.subject}
                      </Box>
                    </Td>
                    <Td py={0} px={1}>{log.recipient_name}</Td>
                    <Td py={0} px={1}>{log.recipient_email}</Td>
                    <Td py={0} px={1}>{log.initiator_name}</Td>
                    <Td py={0} px={1}>
                      <Text
                        color={
                          log.status === 'sent' ? 'green.500' :
                          log.status === 'failed' ? 'red.500' :
                          log.status === 'pending' ? 'orange.500' : 'gray.500'
                        }
                      >
                        {log.status}
                      </Text>
                    </Td>
                    <Td py={0} px={1}>
                      <IconButton
                        icon={<ViewIcon />}
                        size="xs"
                        variant="ghost"
                        onClick={() => handleViewContent(log)}
                        aria-label="View email details"
                      />
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* Email Content Modal */}
      <Modal isOpen={isContentOpen} onClose={() => setIsContentOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalHeader>Email Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody 
            pb={6} 
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                width: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'var(--chakra-colors-gray-300)',
                borderRadius: '24px',
              },
            }}
          >
            {selectedEmail && (
              <VStack align="stretch" spacing={6}>
                {/* Email Details Section */}
                <Box>
                  <Heading size="sm" mb={3}>Email Details</Heading>
                  <Grid templateColumns="120px 1fr" gap={2}>
                    <Text fontWeight="bold">Subject:</Text>
                    <Text>{selectedEmail.subject}</Text>
                    <Text fontWeight="bold">Template:</Text>
                    <Text>{selectedEmail.email_templates?.name || 'N/A'}</Text>
                    <Text fontWeight="bold">Sent At:</Text>
                    <Text>{format(new Date(selectedEmail.sent_at), 'PPpp')}</Text>
                    <Text fontWeight="bold">Status:</Text>
                    <Text color={
                      selectedEmail.status === 'sent' ? 'green.500' :
                      selectedEmail.status === 'failed' ? 'red.500' :
                      selectedEmail.status === 'pending' ? 'orange.500' : 'gray.500'
                    }>{selectedEmail.status}</Text>
                    <Text fontWeight="bold">Message ID:</Text>
                    <Text fontFamily="mono" fontSize="sm">{selectedEmail.message_id}</Text>
                  </Grid>
                </Box>

                {/* Recipient Information */}
                <Box>
                  <Heading size="sm" mb={3}>Recipient Information</Heading>
                  <Grid templateColumns="120px 1fr" gap={2}>
                    <Text fontWeight="bold">Name:</Text>
                    <Text>{selectedEmail.recipient_name}</Text>
                    <Text fontWeight="bold">Email:</Text>
                    <Text>{selectedEmail.recipient_email}</Text>
                    <Text fontWeight="bold">Phone:</Text>
                    <Text>{selectedEmail.variables?.phone}</Text>
                    <Text fontWeight="bold">Contact ID:</Text>
                    <Text fontFamily="mono" fontSize="sm">{selectedEmail.recipient_id}</Text>
                  </Grid>
                </Box>

                {/* Sender Information */}
                <Box>
                  <Heading size="sm" mb={3}>Sender Information</Heading>
                  <Grid templateColumns="120px 1fr" gap={2}>
                    <Text fontWeight="bold">Initiator:</Text>
                    <Text>{selectedEmail.initiator_name}</Text>
                    <Text fontWeight="bold">AWS Sender:</Text>
                    <Text>{selectedEmail.aws_sender}</Text>
                    <Text fontWeight="bold">Club Name:</Text>
                    <Text>{selectedEmail.variables?.clubName}</Text>
                  </Grid>
                </Box>

                {/* Content Preview */}
                <Box>
                  <Heading size="sm" mb={3}>Content</Heading>
                  <Tabs>
                    <TabList>
                      <Tab>Preview</Tab>
                      <Tab>HTML</Tab>
                      <Tab>Text</Tab>
                      <Tab>Variables</Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel>
                        <Box
                          border="1px"
                          borderColor="gray.200"
                          p={4}
                          borderRadius="md"
                          dangerouslySetInnerHTML={{ __html: selectedEmail.content_html }}
                        />
                      </TabPanel>
                      <TabPanel>
                        <Box
                          border="1px"
                          borderColor="gray.200"
                          p={4}
                          borderRadius="md"
                          bg="gray.50"
                          fontFamily="mono"
                          fontSize="sm"
                          whiteSpace="pre-wrap"
                          overflowX="auto"
                        >
                          {selectedEmail.content_html}
                        </Box>
                      </TabPanel>
                      <TabPanel>
                        <Box
                          border="1px"
                          borderColor="gray.200"
                          p={4}
                          borderRadius="md"
                          bg="gray.50"
                          fontFamily="mono"
                          fontSize="sm"
                          whiteSpace="pre-wrap"
                        >
                          {selectedEmail.content_text}
                        </Box>
                      </TabPanel>
                      <TabPanel>
                        <Box
                          border="1px"
                          borderColor="gray.200"
                          p={4}
                          borderRadius="md"
                          bg="gray.50"
                          fontFamily="mono"
                          fontSize="sm"
                        >
                          {Object.entries(selectedEmail.variables || {}).map(([key, value]) => (
                            <Text key={key}>
                              <Text as="span" color="blue.600">{key}</Text>: {JSON.stringify(value)}
                            </Text>
                          ))}
                        </Box>
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </Box>

                {/* Technical Details */}
                <Box>
                  <Heading size="sm" mb={3}>Technical Details</Heading>
                  <Accordion allowToggle>
                    <AccordionItem>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            AWS Response
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel>
                        <Box
                          border="1px"
                          borderColor="gray.200"
                          p={4}
                          borderRadius="md"
                          bg="gray.50"
                          fontFamily="mono"
                          fontSize="sm"
                          whiteSpace="pre-wrap"
                        >
                          {JSON.stringify(selectedEmail.aws_response, null, 2)}
                        </Box>
                      </AccordionPanel>
                    </AccordionItem>
                    {selectedEmail.error_message && (
                      <AccordionItem>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              Error Message
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel>
                          <Alert status="error">
                            <AlertIcon />
                            {selectedEmail.error_message}
                          </Alert>
                        </AccordionPanel>
                      </AccordionItem>
                    )}
                    <AccordionItem>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            Configuration
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel>
                        <Text>Configuration Set: {selectedEmail.configuration_set || 'N/A'}</Text>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Help Modal */}
      <Modal isOpen={isFilterOpen} onClose={onFilterClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Email Audit Log Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={6}>
              <Box>
                <Text fontWeight="bold" mb={2}>Overview</Text>
                <Text>
                  The Email Audit Log provides a comprehensive view of all emails sent through the system,
                  including their delivery status and content.
                </Text>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>Search Capabilities</Text>
                <Text mb={2}>The search box allows you to find emails by:</Text>
                <UnorderedList pl={4} spacing={1}>
                  <ListItem>Email subject</ListItem>
                  <ListItem>Recipient name</ListItem>
                  <ListItem>Recipient email</ListItem>
                  <ListItem>Email content (both HTML and plain text)</ListItem>
                  <ListItem>Delivery status</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>Filters</Text>
                <UnorderedList pl={4} spacing={1}>
                  <ListItem>
                    <Text fontWeight="semibold">Status Filter:</Text>
                    <Text>Shows emails based on their delivery status (Sent, Failed, or Pending)</Text>
                  </ListItem>
                  <ListItem>
                    <Text fontWeight="semibold">Time Filter:</Text>
                    <Text>Filter emails by when they were sent (Today, Last 7 Days, Last 30 Days, or All Time)</Text>
                  </ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>Viewing Email Content</Text>
                <Text>
                  Click the eye icon in the Content column to view the complete email, including both HTML
                  and plain text versions, along with all recipient details.
                </Text>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>Sorting</Text>
                <Text>
                  Click on column headers with ↕ to sort the emails by that field. Click again to reverse
                  the sort order.
                </Text>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default EmailAuditLog;
